/**
 * График как ЗНАЧЕНИЕ: пределы, которые сервер проверяет, и перевод прочитанного
 * графика в тело записи.
 *
 * Отдельно от `api.ts`, потому что это чистые функции без сети — форма отдела считает
 * по ним свои подписи и запрет на «Сохранить», а тест проверяет их, не поднимая
 * запроса.
 *
 * Пределы — серверные (`422` за ними), и продублированы здесь намеренно: `422` из-за
 * порога, набранного руками в поле ввода, ничему не учит, а подпись «0..240» под полем
 * учит сразу.
 */
import type {AttendanceSchedule, DayTimeWire} from '@/entities/personnel/types';
import type {DayTimeInput, ScheduleInput} from './types';

/** Минут в сутках. Сервер принимает и ровно `1440` — это полночь следующего дня. */
export const MAX_DAY_MINUTES = 1440;

/** Все семь дней. Бит 0 — понедельник (порядок `weekday_bit` из sv_control). */
export const FULL_WEEK_MASK = 0b1111111;

/** Пн–пт: умолчание формы, потому что это график почти любого отдела. */
export const WORKWEEK_MASK = 0b0011111;

/** Потолок любого из четырёх порогов, в минутах. */
export const MAX_THRESHOLD_MINUTES = 240;

/**
 * Умолчания порогов для отдела, у которого графика ещё нет.
 *
 * Те же значения, что предлагает личный график сотрудника
 * (`features/manage-personnel/ui/ScheduleEditor`), — иначе один и тот же «график по
 * умолчанию» означал бы в двух местах приложения разное, и первый же отчёт, где личный
 * график перекрыл отдельский, показал бы необъяснимую разницу в опозданиях.
 */
export const DEFAULT_THRESHOLDS = {
  lateGraceMinutes: 5,
  earlyArrivalThresholdMinutes: 30,
  earlyLeaveGraceMinutes: 5,
  overtimeThresholdMinutes: 30,
} as const;

/** Начало и конец смены по умолчанию — 09:00 и 18:00. */
export const DEFAULT_START_MINUTES = 9 * 60;
export const DEFAULT_END_MINUTES = 18 * 60;

const clamp = (value: number, max: number): number =>
  Math.min(max, Math.max(0, Math.round(Number.isFinite(value) ? value : 0)));

/** Минуты внутри суток. */
export const clampDayMinutes = (value: number): number =>
  clamp(value, MAX_DAY_MINUTES);

/** Порог опоздания/переработки. */
export const clampThreshold = (value: number): number =>
  clamp(value, MAX_THRESHOLD_MINUTES);

/** Маска дней в пределах семи бит. */
export const clampMask = (value: number): number =>
  clamp(value, FULL_WEEK_MASK);

/**
 * Прочитанный график → тело записи, уже подрезанное по серверным пределам.
 *
 * `null` на входе — «графика нет», и это законный старт: получится тело с
 * умолчаниями и без времени смены, то есть «заданы только пороги».
 */
export function toScheduleInput(
  schedule: AttendanceSchedule | null | undefined,
): ScheduleInput {
  return {
    startMinutes:
      schedule?.start_minutes == null
        ? null
        : clampDayMinutes(schedule.start_minutes),
    endMinutes:
      schedule?.end_minutes == null ? null : clampDayMinutes(schedule.end_minutes),
    workdaysMask: clampMask(schedule?.workdays_mask ?? WORKWEEK_MASK),
    lateGraceMinutes: clampThreshold(
      schedule?.late_grace_minutes ?? DEFAULT_THRESHOLDS.lateGraceMinutes,
    ),
    earlyArrivalThresholdMinutes: clampThreshold(
      schedule?.early_arrival_threshold_minutes ??
        DEFAULT_THRESHOLDS.earlyArrivalThresholdMinutes,
    ),
    earlyLeaveGraceMinutes: clampThreshold(
      schedule?.early_leave_grace_minutes ??
        DEFAULT_THRESHOLDS.earlyLeaveGraceMinutes,
    ),
    overtimeThresholdMinutes: clampThreshold(
      schedule?.overtime_threshold_minutes ??
        DEFAULT_THRESHOLDS.overtimeThresholdMinutes,
    ),
    dayTimes: dayTimesOf(schedule),
  };
}

/** Своё время дней из прочитанного графика (обёртка proto `{items}`). */
export function dayTimesOf(
  schedule: AttendanceSchedule | null | undefined,
): DayTimeInput[] {
  return (schedule?.day_times?.items ?? [])
    .filter(d => d.weekday >= 0 && d.weekday <= 6)
    .map(d => ({
      weekday: d.weekday,
      startMinutes: clampDayMinutes(d.start_minutes),
      endMinutes: clampDayMinutes(d.end_minutes),
    }));
}

/**
 * Что сохранять: только рабочие по маске дни, и только те, чьё время отличается
 * от общего. День, совпавший с общим временем, ничего не добавляет — а лишняя
 * запись пережила бы смену общего времени и молча оставила бы день по-старому.
 */
export function normalizeDayTimes(
  list: DayTimeInput[],
  mask: number,
  start: number | null,
  end: number | null,
): DayTimeInput[] {
  if (start == null || end == null) {
    return [];
  }
  const seen = new Set<number>();
  return list
    .filter(d => (mask & (1 << d.weekday)) !== 0)
    .filter(d => d.startMinutes !== start || d.endMinutes !== end)
    .filter(d => (seen.has(d.weekday) ? false : (seen.add(d.weekday), true)))
    .sort((a, b) => a.weekday - b.weekday);
}

/**
 * В форму личного графика — обёртка proto, как её ждёт `POST /control/personnel`.
 * Пустой список уходит как `{items: []}`, а не `null`: отсутствие поля сервер читает
 * как «старый клиент, не трогать», и снятое «По дням» иначе не стёрлось бы.
 */
export function toWireDayTimes(list: DayTimeInput[]): {items: DayTimeWire[]} {
  return {
    items: list.map(d => ({
      weekday: d.weekday,
      start_minutes: d.startMinutes,
      end_minutes: d.endMinutes,
    })),
  };
}

/**
 * Что не так с графиком, который человек собрал руками, или `null`, если всё в
 * порядке.
 *
 * Проверок ровно две, и обе — те, из-за которых сервер отвечает `422`:
 *
 * - `halfWindow` — задан один конец смены из двух. Половина графика хуже, чем его
 *   отсутствие: отчёт начнёт считать опоздания по домыслу о второй половине.
 * - `noWorkdays` — пустая маска при заданном времени. Сервер это пропускает, но
 *   отдел без единого рабочего дня означает, что в отчёте у всех его людей каждый
 *   день окажется `unscheduled`, а явка — структурный ноль. Это не ошибка ввода, это
 *   почти всегда забытый флажок, поэтому предупреждение, а не запрет (см. `blocking`).
 *
 * Конца раньше начала здесь НЕТ и быть не должно: это ночная смена.
 */
export type ScheduleProblem = 'halfWindow' | 'noWorkdays';

export function validateSchedule(input: ScheduleInput): ScheduleProblem | null {
  const hasStart = input.startMinutes != null;
  const hasEnd = input.endMinutes != null;
  if (hasStart !== hasEnd) {
    return 'halfWindow';
  }
  if (hasStart && input.workdaysMask === 0) {
    return 'noWorkdays';
  }
  return null;
}

/** Мешает ли проблема сохранению. Сервер откажет только на первой из двух. */
export const blocking = (problem: ScheduleProblem | null): boolean =>
  problem === 'halfWindow';

/** Смена переходит через полночь — законная ночная, но об этом стоит сказать вслух. */
export function isOvernight(input: ScheduleInput): boolean {
  return (
    input.startMinutes != null &&
    input.endMinutes != null &&
    input.endMinutes < input.startMinutes
  );
}

// ---------------------------------------------------------------------------
// Неделя целиком: одна таблица «день → рабочий ли и во сколько» вместо трёх
// разрозненных блоков (дни, общее время, «По дням»). Хранение не меняется: маска,
// общее время и отличия от него — таблица только читает и пишет их.
// ---------------------------------------------------------------------------

/** Готовые недели, которыми начинается почти любой график. */
export const WEEK_PRESETS = {
  weekdays: WORKWEEK_MASK,
  sixDays: 0b0111111,
  everyDay: FULL_WEEK_MASK,
} as const;

export type WeekPreset = keyof typeof WEEK_PRESETS;

/** Какой готовый вариант совпадает с маской, или `null` — неделя своя. */
export function presetOf(mask: number): WeekPreset | null {
  const hit = (Object.keys(WEEK_PRESETS) as WeekPreset[]).find(
    k => WEEK_PRESETS[k] === mask,
  );
  return hit ?? null;
}

export type WeekRow = {
  /** 0 — пн … 6 — вс. */
  weekday: number;
  working: boolean;
  /** Время этого дня: своё или общее; `null` у выходного и у графика без времени. */
  startMinutes: number | null;
  endMinutes: number | null;
  /** У дня своё время, отличное от общего. */
  custom: boolean;
};

/** Семь строк недели из того, что хранится. */
export function weekRows(
  mask: number,
  start: number | null,
  end: number | null,
  dayTimes: DayTimeInput[],
): WeekRow[] {
  return [0, 1, 2, 3, 4, 5, 6].map(weekday => {
    const working = (mask & (1 << weekday)) !== 0;
    const own = dayTimes.find(d => d.weekday === weekday);
    const timed = working && start != null && end != null;
    const custom =
      timed && !!own && (own.startMinutes !== start || own.endMinutes !== end);
    return {
      weekday,
      working,
      startMinutes: timed ? (custom ? own!.startMinutes : start) : null,
      endMinutes: timed ? (custom ? own!.endMinutes : end) : null,
      custom,
    };
  });
}

/**
 * Неделя одной строкой: «Пн–Пт 09:00–18:00 · Сб 09:00–14:00 · Вс выходной».
 * Подряд идущие дни с одинаковым временем склеиваются в диапазон — так человек и
 * говорит о графике, и так его проще проверить глазами перед «Сохранить».
 */
export function describeWeek(
  rows: WeekRow[],
  dayLabel: (weekday: number) => string,
  dayOff: string,
  timeOf: (minutes: number) => string,
): string {
  const key = (r: WeekRow) =>
    r.working
      ? r.startMinutes == null || r.endMinutes == null
        ? 'work'
        : `${timeOf(r.startMinutes)}–${timeOf(r.endMinutes)}`
      : 'off';
  const parts: string[] = [];
  let i = 0;
  while (i < rows.length) {
    let j = i;
    while (j + 1 < rows.length && key(rows[j + 1]) === key(rows[i])) {
      j += 1;
    }
    const days =
      i === j
        ? dayLabel(rows[i].weekday)
        : `${dayLabel(rows[i].weekday)}–${dayLabel(rows[j].weekday)}`;
    const k = key(rows[i]);
    parts.push(k === 'off' ? `${days} ${dayOff}` : k === 'work' ? days : `${days} ${k}`);
    i = j + 1;
  }
  return parts.join(' · ');
}

/**
 * Время одного дня поменяли в таблице. День, вернувшийся к общему времени, из списка
 * отличий уходит — ровно то же, что потом сделает `normalizeDayTimes`, но сразу, чтобы
 * пометка «своё время» в строке гасла в момент совпадения.
 */
export function setDayTime(
  dayTimes: DayTimeInput[],
  weekday: number,
  startMinutes: number,
  endMinutes: number,
  commonStart: number,
  commonEnd: number,
): DayTimeInput[] {
  const rest = dayTimes.filter(d => d.weekday !== weekday);
  if (startMinutes === commonStart && endMinutes === commonEnd) {
    return rest;
  }
  return [...rest, {weekday, startMinutes, endMinutes}].sort((a, b) => a.weekday - b.weekday);
}
